import {
  DdWithCaseFindings,
  PatientSuggestions_PerDiseaseMetrics,
  PatientSuggestionsPerDiseaseMetrics,
  PatientSuggestionsSuggestions,
  QualifiedFeatureFragment,
} from "fe-common/graphql";
import { DDClinicalReasoning } from "fe-common/graphql/dao";
import * as React from "react";
import { useCallback } from "react";
import {getFeatureFlags} from "../featureFlags/getFeatureFlags";

export interface FindingStrength {
  qf: QualifiedFeatureFragment;
  strength: number;
}


const getCalculateFindingPredictives = (
  clinicalReasonings: DDClinicalReasoning[],
): ((cr: DDClinicalReasoning) => FindingStrength[]) => {
  if (!clinicalReasonings?.length || !clinicalReasonings[0]?.findings) {
    return null;
  }

    const useLRsForStrengths = getFeatureFlags("UseLRsForStrengths") as boolean

    const correlated = (finding: DdWithCaseFindings): boolean => (
        useLRsForStrengths
            ? (finding.likelihoodRatio && Math.abs(1 - finding.likelihoodRatio) > 0.2)  //show correlation oly if lr > 1.05 or lr<0.95
            : (finding.absolutePredictiveValue && finding.absolutePredictiveValue !== 1)
    )
    const apvFromLR = (finding: DdWithCaseFindings): number =>
        finding.likelihoodRatio > 1
            ? Math.log(finding.likelihoodRatio)
            : -Math.log(1 / finding.likelihoodRatio);
    const apv = (finding: DdWithCaseFindings): number =>
        useLRsForStrengths ? apvFromLR(finding)
            : finding.absolutePredictiveValue > 1
                ? Math.log10(finding.absolutePredictiveValue)
                : -Math.log10(1 / finding.absolutePredictiveValue);

    const apvs = clinicalReasonings.flatMap((cr) =>
    cr.findings.filter(correlated).map((f) => apv(f)),
  );

  const strengthApvs = apvs.filter((apv) => apv > 0);
  const weaknessApvs = apvs.filter((apv) => apv < 0);

  const maxStrengthApvs = Math.max(...strengthApvs);
  const minStrengthApvs = Math.min(...strengthApvs);

  const maxWeaknessApvs = Math.max(...weaknessApvs);
  const minWeaknessApvs = Math.min(...weaknessApvs);

  return (cr: DDClinicalReasoning): FindingStrength[] => {
    if (!cr || !cr.findings) return [];

    return cr.findings.filter(correlated).map(
      (f) =>
        ({
          qf: f.qf,
          strength:
            apv(f) < 0
              ? (apv(f) - minWeaknessApvs) /
                  (maxWeaknessApvs - minWeaknessApvs) -
                1
              : (apv(f) - minStrengthApvs) /
                (maxStrengthApvs - minStrengthApvs),
        } as FindingStrength),
    );
  };
};

export const useCalculateFindingPredictives = (crs) => {
  return React.useMemo(
    () => (crs ? getCalculateFindingPredictives(crs) : null),
    [crs],
  );
};

function getCalculateSuggestionStrength(
  suggestions: PatientSuggestionsSuggestions[],
): (PatientSuggestionsPerDiseaseMetrics) => number {
  const flatSuggestionsStrength =
    suggestions.flatMap(
      (s) =>
        s.perDiseaseMetrics?.flatMap((pdm) => pdm.findingGivenDisease) || [],
    ) || [];
  const minStrength = Math.min(...flatSuggestionsStrength);
  const maxStrength = Math.max(...flatSuggestionsStrength);

  return (
    suggestionPerDisease: PatientSuggestionsPerDiseaseMetrics,
  ): number => {
    if (minStrength >= maxStrength) return 1;
    return (
      (suggestionPerDisease.findingGivenDisease - minStrength) /
      (maxStrength - minStrength)
    );
  };
}

export const useCalculateSuggestionPredictives = (
  suggestions: PatientSuggestionsSuggestions[],
) =>
  React.useMemo(
    () => suggestions && getCalculateSuggestionStrength(suggestions),
    [suggestions],
  );

export const useCalculateSuggestionReasoning = (
  topDiseases,
  diseasesSubTypesForPlan,
  plan,
) => {
  const calculatePerDiseaseStrength = useCalculateSuggestionPredictives(plan);

  /*Map the reasoning which we have for the subtypes(crs) - to the parents of those subtypes(crs)
   * So the client will show strengthening to the diseases in the list
   * Normalize the diseaseGivenFindings*/
  return useCallback(
    (sDiseaseMetrics: PatientSuggestions_PerDiseaseMetrics[]) => {
      //For each subtype reasoning, get its diseases parents.
      function mapSubTypeToDisease(pdm: PatientSuggestions_PerDiseaseMetrics) {
        return topDiseases.filter((f) =>
          f?.crs
            .map((cr) => cr.disease[0].id)
            .includes(diseasesSubTypesForPlan[pdm.diseaseIndex]?.disease.id),
        );
      }

      if (!sDiseaseMetrics) return [];

      return sDiseaseMetrics
        .flatMap((pdm) =>
          mapSubTypeToDisease(pdm).map((d) => ({
            ...pdm,
            disease: d ? d.qf : null,
            strength: calculatePerDiseaseStrength(pdm),
          })),
        )
        .filter((pdm) => pdm.disease);
    },
    [topDiseases, calculatePerDiseaseStrength, diseasesSubTypesForPlan],
  );
};
